import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Clock } from "lucide-react";
import { fetchAPI } from "@/lib/api";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";

interface TourPlan {
  id: number;
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  duration_nights: number;
  duration_days: number;
  base_price: string;
  plan_type: string;
  featured_image_url: string;
  is_active: number;
}

export default function TourPlans() {
  const [plans, setPlans] = useState<TourPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAPI("?type=tour_plans")
      .then((data: any) => {
        setPlans(data.filter((p: any) => p.is_active));
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching tour plans:", err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading plans...</div>;
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-sans selection:bg-orange-100 selection:text-orange-900">
      <SEO 
        title="Premium Tour Plans" 
        description="Discover our meticulously curated premium travel experiences."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-sand to-background">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-primary text-sm tracking-[0.2em] uppercase font-medium">
              Curated Escapes
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mt-4 mb-6 leading-tight">
              Exclusive <span className="text-primary">Tour Plans</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Discover our meticulously curated premium travel experiences designed 
              for comfort, relaxation, and awe-inspiring discovery.
            </p>
          </div>
        </div>
      </section>

      <main className="py-16 container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <article
              key={plan.id}
              className="group bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-500 hover:-translate-y-2 border border-border/50"
            >
              <div className="relative h-64 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
                <img 
                  src={plan.featured_image_url || "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?auto=format&fit=crop&q=80"} 
                  alt={plan.title} 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-4 right-4 z-20 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full border border-border/50 flex items-center gap-1 shadow-sm">
                  <span className="text-sm font-medium text-foreground">Premium</span>
                </div>
              </div>
              
              <div className="p-6 relative z-20">
                <h3 className="text-xl font-serif font-bold mb-2 group-hover:text-primary transition-colors text-foreground">
                  {plan.title}
                </h3>
                {plan.subtitle && (
                  <p className="text-sm text-muted-foreground mb-4 italic">"{plan.subtitle}"</p>
                )}
                
                <div className="space-y-2 text-sm text-muted-foreground mb-6">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-primary" />
                    <span>{plan.duration_nights} Nights / {plan.duration_days} Days</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span className="truncate">{plan.plan_type}</span>
                  </div>
                </div>

                <div className="mt-auto pt-4 border-t border-border flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Starting from</p>
                    <p className="text-lg font-bold text-foreground">₹{plan.base_price}</p>
                  </div>
                  <Link to={`/tour-plans/${plan.slug}`}>
                    <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80 group-hover:-translate-x-1 transition-transform gap-2">
                      View details <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
